import React, { useState, useEffect } from "react";
import "./CheckoutModal.css";
import { useCart } from "../context/CartContext";
import { useUser } from "../context/UserContext";
import { useCheckout } from "../context/CheckoutContext";
import { useProfile } from "../context/ProfileContext";

function CheckoutModal({ onClose, onViewOrders }) {
  const [stage, setStage] = useState(0);
  const [selectedCheckoutType, setSelectedCheckoutType] = useState(null);
  const [isInRestaurant, setIsInRestaurant] = useState(null);
  const [tableNumber, setTableNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { cartItems, clearCart } = useCart();
  const { user } = useUser();
  const { isOpen, type, isReorder, reorderOrderId } = useCheckout();
  const { openProfile } = useProfile();

  useEffect(() => {
    setCustomerName(user?.name || "");
    setSelectedCheckoutType(type);
  }, [user, type]);

  useEffect(() => {
    if (!isOpen) {
      setStage(0);
      setSelectedCheckoutType(null);
      setIsInRestaurant(null);
      setTableNumber("");
      setNotes("");
      setSuccess(false);
      setIsLoading(false);
    } else {
      setStage(type ? 1 : 0);
    }
  }, [isOpen, type]);

  if (!isOpen) return null;

  const goBack = () => {
    if (stage === 2 || stage === 3) setStage(1);
    else if (stage === 1 && !selectedCheckoutType) setStage(0);
  };

  const handleSelectOrderType = (type) => {
    setSelectedCheckoutType(type);
    setStage(1);
  };

  const handleEatInStage1Answer = (answer) => {
    setIsInRestaurant(answer);
    if (answer) setStage(2);
    else setStage(3);
  };

  const placeOrder = async () => {
    setIsLoading(true);

    if (!isReorder && !cartItems.length) {
      alert("Your cart is empty.");
      setIsLoading(false);
      return;
    }

    const endpoint = isReorder
      ? "/php/reorder.php"
      : "/php/place-order.php";

    const payload = isReorder
      ? {
          customer_id: user?.id || null,
          customer_name: customerName.trim(),
          order_type: selectedCheckoutType === "eat-in" ? "Eat In" : "Pick Up",
          table_number: selectedCheckoutType === "eat-in" ? tableNumber.trim() : null,
          notes: notes.trim(),
          original_order_id: reorderOrderId,
        }
      : {
          customer_id: user?.id || null,
          customer_name: customerName.trim(),
          order_type: selectedCheckoutType === "eat-in" ? "Eat In" : "Pick Up",
          table_number: selectedCheckoutType === "eat-in" ? tableNumber.trim() : null,
          notes: notes.trim(),
          items: cartItems.map((c) => ({
            item_id: c.item?.id || c.item_id,
            base_price: c.item?.price || 0,
            required_option_id: c.selectedRequiredOption?.id || null,
            required_option_name: c.selectedRequiredOption?.name || "",
            required_option_price: c.selectedRequiredOption?.price || 0,
            quantity: c.quantity || 1,
            selectedOptionalOptions: c.selectedOptionalOptions || [],
            swallowOptions: c.item?.swallowOptions || [],
            mainProteins: c.item?.mainProteins || [],
            extraProteins: c.item?.extraProteins || [],
          })),
        };

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        clearCart();
        setSuccess(true);
      } else {
        console.error("Order failed:", result.message || result);
        alert("Failed to place order. Please try again.");
      }
    } catch (err) {
      console.error("Error sending order:", err);
      alert("Error sending order. Check console for details.");
    }

    setIsLoading(false);
  };

  return (
    <div className="checkout-modal-overlay">
      <div className="checkout-modal">
        <div className="checkout-modal-top modal-top">
          {stage > 1 && !success ? (
            <i className="fas fa-arrow-left back-icon" onClick={goBack}></i>
          ) : (
            <div className="back-placeholder"></div>
          )}
          <button className="close-modal-button" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <h2 className="checkout-heading">
          {!success && stage === 0 && "Select Order Type"}
          {!success && selectedCheckoutType === "eat-in" && stage === 1 && "Eat In Checkout"}
          {!success && selectedCheckoutType === "pickup" && stage === 1 && "Pick Up Checkout"}
          {!success && selectedCheckoutType === "eat-in" && stage === 2 && "Customer Information"}
          {!success && selectedCheckoutType === "eat-in" && stage === 3 && "Cannot Order"}
          {success && "Order Received"}
        </h2>

        {/* Stage 0 */}
        {!success && stage === 0 && (
          <div className="stage-content">
            <h3>How would you like your order?</h3>
            <div className="button-group">
              <button className="add-to-cart-button" onClick={() => handleSelectOrderType("eat-in")}>Eat In</button>
              <button className="add-to-cart-button" onClick={() => handleSelectOrderType("pickup")}>Pick Up</button>
            </div>
          </div>
        )}

        {/* Eat-in stage 1 */}
        {!success && selectedCheckoutType === "eat-in" && stage === 1 && (
          <div className="stage-content">
            <h3>Are you currently in the restaurant?</h3>
            <div className="button-group">
              <button className="add-to-cart-button" onClick={() => handleEatInStage1Answer(true)}>Yes</button>
              <button className="add-to-cart-button" onClick={() => handleEatInStage1Answer(false)}>No</button>
            </div>
          </div>
        )}

        {/* Eat-in stage 2: in restaurant */}
        {!success && selectedCheckoutType === "eat-in" && stage === 2 && (
          <form className="checkout-form" onSubmit={(e) => { e.preventDefault(); placeOrder(); }}>
            <label>Your Name</label>
            <input type="text" value={customerName} readOnly required />
            <label>Table Number</label>
            <input type="text" value={tableNumber} onChange={(e) => setTableNumber(e.target.value)} required />
            <label>Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
            <button type="submit" className="add-to-cart-button send-button" disabled={!tableNumber.trim() || isLoading}>
              {isLoading ? "Sending Order..." : "Send Order To Kitchen"}
            </button>
          </form>
        )}

        {/* Eat-in stage 3: not in restaurant */}
        {!success && selectedCheckoutType === "eat-in" && stage === 3 && (
          <p className="not-in-restaurant-message">Please order when you're in the restaurant.</p>
        )}

        {/* Pickup stage */}
        {!success && selectedCheckoutType === "pickup" && stage === 1 && (
          <form className="checkout-form" onSubmit={(e) => { e.preventDefault(); placeOrder(); }}>
            <label>Your Name</label>
            <input type="text" value={customerName} readOnly required />
            <label>Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
            <button type="submit" className="add-to-cart-button send-button" disabled={isLoading || !customerName.trim()}>
              {isLoading ? "Sending Order..." : "Send Order To Kitchen"}
            </button>
          </form>
        )}

        {/* Success stage */}
        {success && (
          <div className="order-success">
            <h3>Your order has been received and is being processed.</h3>
            <button className="add-to-cart-button send-order-button" onClick={() => { openProfile("orders"); onClose(); }}>
              Click here to view your orders
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default CheckoutModal;